import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/Collapsible';
import { inputVariants } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import type { OnDeviceMcpCatalogEntry } from '@/features/integrations/integrations.types';
import { getIntegrationIcon } from '@/integrations/integration-icons';

import { Callout } from './callout';
import { DialogHeader } from './dialog-header';
import { FieldLabel } from './field-label';
import {
  describeLogseqProblem,
  LOGSEQ_SETUP_STEPS,
  type LogseqConnection,
  type LogseqProblem,
} from './logseq-setup';
import { ModalCard } from './modal-card';
import { SetupSteps } from './setup-steps';

import type {
  TestedConnectForm,
  TestedConnectProblem,
} from './use-tested-connect';

type LogseqConnectForm = TestedConnectForm<LogseqConnection, LogseqProblem>;

const INPUT_CLASS = inputVariants({ inputSize: 'sm' });

function problemCopy(
  form: LogseqConnectForm,
  problem: TestedConnectProblem<LogseqProblem>
) {
  if (problem === 'save_failed') {
    return 'Logseq connected, but Littlebird couldn’t finish saving it. Try again.';
  }
  if (problem === 'already_connected') return 'Logseq is already connected.';
  return describeLogseqProblem(problem, form.connection, { inSetup: true });
}

export const LogseqConnectModal = ({
  catalogEntry,
  form,
  onCancel,
}: {
  catalogEntry: OnDeviceMcpCatalogEntry;
  form: LogseqConnectForm;
  onCancel: () => void;
}): React.ReactElement => {
  const LogseqIcon = getIntegrationIcon('logseq');
  const { connection, changeConnection } = form;

  return (
    <ModalCard onClose={onCancel} closeDisabled={!form.canCancel}>
      <DialogHeader
        icon={<LogseqIcon className="size-5" />}
        title="Connect Logseq"
        subtitle={<span className="block">{catalogEntry.description}</span>}
      />
      <YStack className="gap-5 p-6">
        <SetupSteps label="In Logseq" steps={LOGSEQ_SETUP_STEPS} />

        <YStack className="gap-2">
          <FieldLabel>API token</FieldLabel>
          <XStack className="gap-2">
            <input
              type="password"
              aria-label="API token"
              placeholder="Paste your Logseq API token"
              value={connection.token}
              disabled={form.busy}
              onChange={(event) =>
                changeConnection({ token: event.target.value })
              }
              autoComplete="off"
              spellCheck={false}
              className={INPUT_CLASS}
            />
            <Button
              variant="outline"
              size="sm"
              className="h-8 shrink-0"
              disabled={!form.canTest}
              isLoading={form.status === 'testing'}
              loadingText="Testing…"
              onClick={() => void form.test()}
            >
              Test
            </Button>
          </XStack>
        </YStack>

        <Collapsible>
          <CollapsibleTrigger className="group flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground">
            <Icons.chevronRight className="size-3.5 transition-transform group-data-[state=open]:rotate-90" />
            Advanced: host and port
          </CollapsibleTrigger>
          <CollapsibleContent>
            <XStack className="gap-3 pt-3">
              <YStack className="flex-1 gap-1.5">
                <FieldLabel>Host</FieldLabel>
                <input
                  aria-label="Host"
                  value={connection.host}
                  disabled={form.busy}
                  onChange={(event) =>
                    changeConnection({ host: event.target.value })
                  }
                  spellCheck={false}
                  className={INPUT_CLASS}
                />
              </YStack>
              <YStack className="w-28 gap-1.5">
                <FieldLabel>Port</FieldLabel>
                <input
                  aria-label="Port"
                  inputMode="numeric"
                  value={connection.port}
                  disabled={form.busy}
                  onChange={(event) =>
                    changeConnection({ port: event.target.value })
                  }
                  spellCheck={false}
                  className={INPUT_CLASS}
                />
              </YStack>
            </XStack>
          </CollapsibleContent>
        </Collapsible>

        {form.problem ? (
          <Callout tone="danger" icon={<Icons.warn className="size-4" />}>
            {problemCopy(form, form.problem)}
          </Callout>
        ) : form.status === 'passed' ? (
          <XStack className="items-center gap-2.5 rounded-lg border border-border bg-surface px-3 py-2.5">
            <span className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-success-3 text-success-11">
              <Icons.check className="size-3.5" />
            </span>
            <span className="text-sm text-foreground">Connected to Logseq</span>
          </XStack>
        ) : null}
      </YStack>
      <XStack className="items-center justify-end gap-2 border-t border-border p-4 px-6">
        <Button
          variant="ghost"
          size="sm"
          disabled={!form.canCancel}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={!form.canConnect}
          isLoading={form.status === 'saving'}
          loadingText="Connecting…"
          onClick={() => void form.connect()}
        >
          Connect
        </Button>
      </XStack>
    </ModalCard>
  );
};
